import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Search, Phone } from "lucide-react";
import { api, fmtErr } from "@/lib/api";
import { track } from "@/lib/track";
import { useAuth } from "@/context/AuthContext";
import { Reveal, Eyebrow, EASE } from "./Reveal";

const EMPTY = { name: "", email: "", item: "", brand: "", size: "", budget: "", notes: "" };

const Field = ({ label, k, form, set, type = "text", required, area, placeholder }) => (
  <label className={`block ${area ? "sm:col-span-2" : ""}`}>
    <span className="font-mono-j text-[11px] uppercase tracking-[0.25em] text-[#a78bce]">{label}{required && " *"}</span>
    {area ? (
      <textarea value={form[k]} onChange={(e) => set({ ...form, [k]: e.target.value })} rows={3} placeholder={placeholder} data-testid={`request-${k}-input`} className="mt-2 w-full resize-none border-b border-[#3b1d63] bg-transparent py-3 text-[#f3eefc] outline-none transition-colors placeholder:text-[#7d6a9c] focus:border-[#d8b4fe]" />
    ) : (
      <input type={type} required={required} value={form[k]} onChange={(e) => set({ ...form, [k]: e.target.value })} placeholder={placeholder} data-testid={`request-${k}-input`} className="mt-2 w-full border-b border-[#3b1d63] bg-transparent py-3 text-[#f3eefc] outline-none transition-colors placeholder:text-[#7d6a9c] focus:border-[#d8b4fe]" />
    )}
  </label>
);

export const RequestSection = () => {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/requests", form);
      track("request_submitted");
      setDone(true);
      toast.success("Request sent — we'll start the hunt.");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="request" className="relative overflow-hidden scroll-mt-20" data-testid="request-section">
      <div className="pointer-events-none absolute right-[-10%] top-10 h-[480px] w-[480px] rounded-full bg-[#7c3aed]/20 blur-[140px]" />
      <div className="relative mx-auto grid max-w-[1400px] gap-16 px-5 py-28 md:px-10 md:py-40 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <Eyebrow>Request an item</Eyebrow>
          <h2 className="font-display mt-5 text-5xl leading-[0.95] text-[#f3eefc] md:text-7xl">Don't see it? <span className="italic text-[#d8b4fe]">We'll hunt it down.</span></h2>
          <p className="mt-8 max-w-md text-[#cbbbe6]">Looking for a specific bag, sneaker, fragrance or jacket we don't have listed? Tell us what you want and we'll try to source it for you. When we find it, you get first dibs.</p>
          <div className="mt-10 flex items-center gap-3 text-sm text-[#e9ddfb]"><Search className="h-4 w-4 text-[#a855f7]" /> Any brand, any size, any category</div>
          <div className="mt-10 flex items-center gap-4">
            <a href="sms:+16473754053" data-testid="request-text-link" aria-label="Text us — text only line" className="grid h-12 w-12 place-items-center rounded-full bg-[#7c3aed] text-white transition-transform duration-300 hover:scale-110 hover:bg-[#a855f7]">
              <Phone className="h-5 w-5" />
            </a>
            <span className="font-mono-j text-[11px] uppercase tracking-[0.25em] text-[#a78bce]">Prefer to text? Tap the phone — text only</span>
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-7">
          {done ? (
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ ease: EASE, duration: 0.8 }} className="chamfer border border-[#3b1d63] bg-[#110720] p-10 md:p-14" data-testid="request-success">
              <span className="grid h-14 w-14 place-items-center rounded-full bg-[#7c3aed]"><Check className="h-6 w-6 text-white" /></span>
              <h3 className="font-display mt-8 text-4xl text-[#f3eefc]">Request received.</h3>
              <p className="mt-4 max-w-md text-[#cbbbe6]">We'll email <span className="text-[#f3eefc]">{form.email}</span> as soon as we track down your {form.item}.</p>
              <button onClick={() => { setDone(false); setForm({ ...EMPTY, name: form.name, email: form.email }); }} data-testid="request-another-button" className="mt-10 text-sm text-[#d8b4fe] underline underline-offset-8">Request another item</button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="chamfer grid gap-8 border border-[#281245] bg-[#0b0514]/80 p-8 backdrop-blur-xl sm:grid-cols-2 md:p-12" data-testid="request-form">
              <Field label="Your name" k="name" form={form} set={setForm} required />
              <Field label="Email" k="email" type="email" form={form} set={setForm} required />
              <Field label="What are you looking for?" k="item" form={form} set={setForm} required area placeholder="shoes, bags, colognes…" />
              <Field label="Brand" k="brand" form={form} set={setForm} placeholder="" />
              <Field label="Size" k="size" form={form} set={setForm} placeholder="M / US 9 / 50ml" />
              <Field label="Budget (CAD)" k="budget" form={form} set={setForm} placeholder="$300–$500" />
              <Field label="Anything else?" k="notes" form={form} set={setForm} placeholder="Colour, condition, deadline…" />
              <button disabled={busy} data-testid="request-submit-button" className="sm:col-span-2 rounded-full bg-[#7c3aed] px-8 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-[#a855f7] disabled:opacity-60">
                {busy ? "Sending…" : "Send my request"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
